import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useMemo } from 'react'
import type { JsonValue } from 'type-fest'
import type {
  DecorationType,
  UserBaseFragment,
} from 'src/services/apollo/generated/graphql'
import { getUserActiveDecoration } from '../../features/UserDecoration/utils/activeDecoration'
import { baseApolloClient } from '../apollo/client'
import {
  ListMyActiveDecorationsDocument,
  ListMyDecorationsDocument,
  ListUserActiveDecorationsDocument,
  ListUserDecorationsDocument,
} from '../apollo/generated/graphql'
import { useToggleDecoration } from '../apollo/user-decoration'
import { orpcClient } from './client'
import { contractClient } from './contract'
import { loadDecoration } from './decorationBatcher'
import {
  userGetMyDecorationsOptions,
  userGetMyDecorationsQueryKey,
} from './generated/@tanstack/react-query.gen'
import type { UserGetMyDecorationsResponse } from './generated/types.gen'

/**
 * Minimal structural view of a decoration. Both the oRPC `BatchDecoration`
 * DTO and the GraphQL `Decoration` type satisfy it, so components can accept
 * either without unsafe casts.
 *
 * `data` is `JsonValue`, not `unknown`: the field originates from a server
 * JSON column (Prisma JSON → GraphQL `JSONObject` / oRPC `z.unknown()`) and
 * is always JSON-shaped on the wire. The narrower type documents that
 * contract for downstream parsers; runtime safety still comes from
 * `valibot.safeParse` at the call sites.
 */
export type DecorationItem = {
  id: string
  type: DecorationType
  data?: JsonValue
}

type ActiveDecorationUser = Pick<UserBaseFragment, 'activeDecorations'> & {
  /** Populated by `mapAuthor` on v2 oRPC author payloads; absent on GraphQL flows. */
  activeDecorationIds?: string[] | null
}

export const useMyActiveDecorations = (opts?: { enabled?: boolean }) =>
  useQuery({
    ...userGetMyDecorationsOptions({
      client: orpcClient,
    }),
    enabled: opts?.enabled ?? true,
  })

const myActiveDecorationsQueryKey = () =>
  userGetMyDecorationsQueryKey({ client: orpcClient })

export function useToggleActiveDecoration() {
  const queryClient = useQueryClient()
  const queryKey = myActiveDecorationsQueryKey()

  return useToggleDecoration({
    onCompleted(data) {
      const { id, isEnabled } = data?.toggleDecorationV2 ?? {}
      if (id == null || isEnabled == null) return

      if (!isEnabled) {
        // Disabled: immediately remove from active list
        queryClient.setQueryData<UserGetMyDecorationsResponse>(queryKey, old =>
          old?.filter(it => it.id !== id),
        )
      } else {
        // Enabled: need full data from server
        queryClient.invalidateQueries({ queryKey })
      }
    },
  })
}

export const useUpdateUserDecorationRelation = () => {
  return useMutation({
    mutationFn: (data: { decorationRelationId: string; userRemark: string }) =>
      // Forward the string id as-is; the contract's `z.coerce.bigint()`
      // coerces on the server. A client-side `BigInt(...)` would throw
      // synchronously on non-integer input, bypassing the zod error path.
      // `number` isn't an option — relation ids are bigint-backed and can
      // exceed `Number.MAX_SAFE_INTEGER`.
      contractClient.userDecorationRelation.updateUserDecorationRelation({
        id: data.decorationRelationId,
        userRemark: data.userRemark,
      }),
    onSuccess: (_data, _variables, _onMutateResult, context) => {
      // Invalidate all decoration-related queries to refresh the cache
      baseApolloClient.refetchQueries({
        include: [
          ListUserDecorationsDocument,
          ListMyDecorationsDocument,
          ListUserActiveDecorationsDocument,
          ListMyActiveDecorationsDocument,
        ],
      })
      context.client.invalidateQueries({
        queryKey: myActiveDecorationsQueryKey(),
      })
    },
  })
}

/**
 * Hydrate decorations by id via /v2/decoration/batch. Each id is its own
 * React Query cache entry under `['decorationItem', id]`, so concurrent
 * calls across cards dedupe at the cache layer. Network fetches are
 * coalesced by `decorationBatcher.loadDecoration` into one batch RPC per
 * microtask (DataLoader chunks at the server's 50-id cap).
 *
 * React Query owns freshness: any `invalidateQueries` on this key triggers
 * a real refetch (DataLoader's own cache is disabled so it can't shadow
 * invalidations). Server-omitted ids cache as `null` for the same window.
 */
export function useDecorations(
  decorationIds: string[],
): Map<string, DecorationItem> {
  return useQueries({
    queries: decorationIds.map(id => ({
      queryKey: ['decorationItem', id] as const,
      queryFn: () => loadDecoration(id),
      staleTime: 15 * 60_000,
      // Align with endpoint TTL; default gcTime (5min) would evict early
      // and force re-fetches.
      gcTime: 15 * 60_000,
    })),
    // Stabilize the returned Map ref via RQ's structural sharing so
    // downstream `useMemo` consumers can actually cache-hit.
    combine: results => {
      const map = new Map<string, DecorationItem>()
      for (let i = 0; i < decorationIds.length; i++) {
        const item = results[i]?.data
        if (!item) continue
        // `BatchDecoration.data` is `unknown` on the wire; narrow once here
        // so `DecorationItem` consumers don't restate the cast.
        map.set(decorationIds[i], {
          id: item.id,
          type: item.type,
          data: item.data as JsonValue | undefined,
        })
      }
      return map
    },
  })
}

/**
 * Group an author's active decorations by type, hydrating via
 * /v2/decoration/batch. If the server ever returns multiple decorations of
 * the same type (not expected — the toggle endpoint enforces one-per-type),
 * the first in input order wins.
 */
export function useAuthorActiveDecorations(
  activeDecorationIds: string[] | undefined | null,
): Map<DecorationType, DecorationItem> {
  const ids = useMemo(() => activeDecorationIds ?? [], [activeDecorationIds])
  const byId = useDecorations(ids)

  return useMemo(() => {
    const byType = new Map<DecorationType, DecorationItem>()
    for (const id of ids) {
      const item = byId.get(id)
      if (!item) continue
      if (!byType.has(item.type)) byType.set(item.type, item)
    }
    return byType
  }, [ids, byId])
}

/**
 * Resolve a user's currently-active decoration of `type` regardless of
 * source: v2 oRPC payloads (with `activeDecorationIds`) hydrate via the
 * batch endpoint; GraphQL flows (profile pages etc.) read from the inline
 * `activeDecorations` array. Callers don't need to branch.
 */
export function useUserActiveDecoration(
  user: ActiveDecorationUser | null | undefined,
  type: DecorationType,
): DecorationItem | undefined {
  const byType = useAuthorActiveDecorations(user?.activeDecorationIds)
  if (user?.activeDecorationIds != null) return byType.get(type)
  return getUserActiveDecoration(user ?? undefined, { type })?.decoration
}
